import { useEffect, useState, useMemo } from 'react';
import { message } from 'antd';
import { useQuery, gql, useLazyQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import {
	REVERSE_MAPING_BADGE_TYPE,
	ICON_COMPLETE_MAP,
} from '../../RnRCommonComponent/CoreBadge/rewardsConstants.js';
import { datediff } from '../utils.js';

const QUERY_NOMINATION_CONFIGURATIONS = gql`
	query QueryNominationConfigurations(
		$companyId: ID!
		$limit: Int
		$nextToken: String
	) {
		queryNominationConfigurationsByCompanyId(
			companyId: $companyId
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				awardedCount
				badge {
					allowSelfNomination
					assignmentLevelId
					assignmentLimit
					assignmentSSORequiredId
					badgeId
					automationRule
					badgeRotation
					badgeTypeId
					bonusPoints
					category
					color
					companyId
					createdAt
					createdBy
					description
					gradientColor
					iconId
					id
					image
					isAssignmentSSORequired
					isAssignmentRestricted
					isPreApproved
					isRecipientSSORequired
					isRewardRequiresDate
					label
					physicalAwardActivityId
					name
					points
					recipientLevelId
					recipientPayTypeId
					recipientSSORequiredId
					rewardBonus
					rewardMaxBonus
					shape
					snappyCampaignTierId
					snappyGrantId
					updatedAt
					updatedBy
					visibility
					customImageKey
					customImageSignedUrl
					labelColor
					customIconKey
					customIconSignedUrl
					snappyGrant {
						costInCurrency
						id
						name
						description
						externalSnappyId
						snappyCampaignId
						snappyCampaignTierId
					}
					snappyCampaignTier {
						id
						description
						name
						snappyCampaignId
						totalCost
						pointsRequired
					}
				}
				badgeId
				companyId
				configId
				createdAt
				customFormId
				endDate
				id
				itemType
				nominationConfigurationId
				nominatorEmployeeLevelId
				nominatorEmployeePayTypeId
				nominatorEmployeelogInTypeId
				nomineeEmployeeLevelId
				nomineeEmployeePayTypeId
				nomineeEmployeelogInTypeId
				startDate
				status
				updatedAt
			}
			nextToken
		}
	}
`;

const QUERY_NOMINATIONS = gql`
	query QueryNominationsByConfigId(
		$configId: ID!
		$companyId: ID!
		$isWinner: Boolean
		$limit: Int
		$nextToken: String
	) {
		queryNominationsByConfigId(
			configId: $configId
			companyId: $companyId
			isWinner: $isWinner
			limit: $limit
			nextToken: $nextToken
		) {
			items {
				companyId
				configId
				createdAt
				customFormAnswers
				id
				isWinner
				itemType
				nominationConfigurationId
				nominationId
				nominationReason
				nominator {
					id
					firstName
					lastName
					title
					employeeId
					departmentId
				}
				nominatorId
				nominee {
					firstName
					id
					lastName
					title
					userGroupId
					subCompanyId
					employeeId
					departmentId
				}
				nomineeId
				reason
				updatedAt
			}
			nextToken
		}
	}
`;

const useGetNominatedConfigList = () => {
	const [nominatedBadgeList, setNominatedBadgeList] = useState([]);
	const [openNominationsList, setOpenNominationsList] = useState([]);
	const [submittedNominationsList, setSubmittedNominationsList] = useState([]);
	const [completeNominationsList, setCompleteNominationsList] = useState([]);
	const [winningNominations, setWinningNominations] = useState({});
	const [nominationsData, setNominationsData] = useState({});
	const [nominationsLoading, setNominationsLoading] = useState(true);
	const [winningNominationsLoading, setWinningNominationsLoading] =
		useState(false);
	const currentUser = useSelector((state) => state?.user?.currentUser);

	const { data, loading, error, refetch } = useQuery(
		QUERY_NOMINATION_CONFIGURATIONS,
		{
			variables: { companyId: currentUser?.companyId, limit: 1000 },
			skip: !currentUser?.companyId,
			fetchPolicy: 'cache-and-network',
		}
	);

	const [getNominations] = useLazyQuery(QUERY_NOMINATIONS, {
		fetchPolicy: 'cache-and-network',
	});

	const processedRecords = useMemo(() => {
		if (!data?.queryNominationConfigurationsByCompanyId?.items) return [];

		return data.queryNominationConfigurationsByCompanyId.items.map((config) => {
			// Transform badge data to include badgeType and icon mappings
			let enrichedBadge = config.badge || {};

			if (config.badge) {
				const badgeTypeCode =
					REVERSE_MAPING_BADGE_TYPE[config.badge.badgeTypeId] || '';

				let iconInfo = null;
				if (config.badge.iconId) {
					iconInfo = ICON_COMPLETE_MAP.find(
						(icon) => icon.id === config.badge.iconId
					);
				}

				enrichedBadge = {
					...config.badge,
					badgeType: { code: badgeTypeCode },
					icon: iconInfo ? { code: iconInfo.code } : null,
					iconInfo,
				};
			}

			return {
				id: config.id,
				companyId: config.companyId,
				configId: config.configId,
				createdAt: config.createdAt,
				updatedAt: config.updatedAt,
				status: config.status,
				badge: enrichedBadge,
				badgeId: config.badgeId,
				customFormId: config.customFormId,
				startDate: config.startDate,
				endDate: config.endDate,
				awardedCount: config.awardedCount || 0,
				nomineeEmployeeLevelId: config.nomineeEmployeeLevelId || [],
				nomineeEmployeelogInTypeId: config.nomineeEmployeelogInTypeId,
				nomineeEmployeePayTypeId: config.nomineeEmployeePayTypeId,
				nominatorEmployeePayTypeId: config.nominatorEmployeePayTypeId,
				nominatorEmployeelogInTypeId: config.nominatorEmployeelogInTypeId,
				nominatorEmployeeLevelId: config.nominatorEmployeeLevelId || [],
				nominated: nominationsData[config.id]?.length || 0,
				winnerDetails: [],
			};
		});
	}, [data, nominationsData]);

	useEffect(() => {
		if (
			!data?.queryNominationConfigurationsByCompanyId?.items ||
			!currentUser?.companyId
		)
			return;

		const fetchAllNominations = async () => {
			setNominationsLoading(true);
			const newNominationsData = {};

			for (const config of data.queryNominationConfigurationsByCompanyId
				.items) {
				try {
					const { data: nominationsResult } = await getNominations({
						variables: {
							configId: config.id,
							companyId: currentUser.companyId,
							limit: 1000,
						},
					});

					newNominationsData[config.id] =
						nominationsResult?.queryNominationsByConfigId?.items || [];
				} catch (error) {
					console.error(
						`Error fetching nominations for config ${config.id}:`,
						error
					);
					newNominationsData[config.id] = [];
				}
			}

			setNominationsData(newNominationsData);
			setNominationsLoading(false);
		};

		fetchAllNominations();
	}, [data, currentUser?.companyId, getNominations]);

	useEffect(() => {
		if (processedRecords.length === 0 || nominationsLoading) return;

		const nominationConfigurationOpenList =
			processedRecords.filter(({ status, endDate }) => {
				if (status !== 'Open') return false;
				const daysRemaining = datediff(new Date(), new Date(endDate));
				return daysRemaining > 0;
			}) || [];

		const nominationConfigurationCompleteList =
			processedRecords.filter(({ status }) => status === 'Complete') || [];
		const nominationConfigurationSubmittedList =
			processedRecords.filter(({ status }) => status === 'Submitted') || [];

		// Set individual filtered lists
		setOpenNominationsList(nominationConfigurationOpenList);
		setSubmittedNominationsList(nominationConfigurationSubmittedList);
		setCompleteNominationsList(
			nominationConfigurationCompleteList.filter(
				({ nominated }) => nominated > 0
			) || []
		);

		// Admin table shows every configuration with its real, backend-managed
		// status (Pending/Open/Closed/Complete/Submitted).
		setNominatedBadgeList(processedRecords);
	}, [processedRecords]);

	useEffect(() => {
		const fetchWinningNominations = async (config) => {
			try {
				const { data: winningNominations } = await getNominations({
					variables: {
						isWinner: true,
						configId: config.id,
						companyId: currentUser.companyId,
						limit: 1000,
					},
				});
				return winningNominations?.queryNominationsByConfigId?.items || [];
			} catch (error) {
				console.error(
					`Error fetching nominations for config ${config.id}:`,
					error
				);
				return [];
			}
		};

		const fetchAndSetWinningNominations = async () => {
			setWinningNominationsLoading(true);
			const winningNominationsNew = { ...winningNominations };
			for (const completeNomination of completeNominationsList) {
				winningNominationsNew[completeNomination.id] =
					await fetchWinningNominations(completeNomination);
			}
			setWinningNominations(winningNominationsNew);
			setWinningNominationsLoading(false);
		};

		if (completeNominationsList.length > 0 && !winningNominationsLoading) {
			fetchAndSetWinningNominations();
		}
	}, [completeNominationsList.length]);

	const getNominationConfiguration = async () => {
		try {
			await refetch();
		} catch (error) {
			message.error('Failed to fetch recognitions list! Try again later.');
		}
	};

	if (error) {
		message.error('Failed to fetch recognitions list! Try again later.');
	}

	return {
		nominatedBadgeListLoader: loading || nominationsLoading,
		nominatedBadgeList,
		openNominationsList,
		submittedNominationsList,
		completeNominationsList,
		winningNominations,
		getNominationConfiguration,
	};
};

export default useGetNominatedConfigList;
